import {
  REPORT_IDS,
  REPORT_MAX_RANGE_DAYS,
  REPORT_PERMISSIONS,
  type CommunicationsReport,
  type FunnelReport,
  type InsurancePaymentsReport,
  type IntegrationHealthReport,
  type MetricDefinition,
  type ReportBucket,
  type ReportCell,
  type ReportChart,
  type ReportColumn,
  type ReportEnvelope,
  type ReportGroupBy,
  type ReportId,
  type ReportRange,
  type ReportSeriesRef,
  type ReportTable,
} from '@platform/contracts';

/**
 * The four clinic reports, as the API declares them (MASTER_PLAN M11.7, FR-ADMIN-10,
 * FR-REPORT-01..05, `Docs/API.md` §2.11, rules §51).
 *
 * **Every shape here is `@platform/contracts` `reports.ts` and `analytics.ts`.** The
 * bespoke payload types this file used to declare — `FunnelReport`, `Series`,
 * `UnchargedVisit`, `SuppressedContact`, `IntegrationReportRow` and the rest — are gone.
 * M11.6 landed one generic envelope for all four reports rather than four typed payloads
 * (`ADMIN_REPORTS_UI.md` §7A.4), so a local copy of them would now be a second, wrong
 * source of truth for shapes the server owns.
 *
 * What is left in this file is the part the *app* owns and the server has no opinion
 * about: what each report is called, what route it lives at, and which permission key
 * opens it.
 *
 * ## `integration-health`, not `integrations`
 *
 * `ReportId` is the API's spelling, because it is the API's identifier: it is the path
 * segment, the `report` field on every payload and the `:report` of the export route.
 * The *route* in this app is still `/reports/integrations`, because that is what a
 * practice manager calls it — `REPORT_PATHS` below is the one place the two are mapped.
 *
 * **No tenant id appears anywhere in this file and none may.** The API resolves the
 * clinic from the host; a tenant id in a report query is ignored and logged
 * (API.md §1.4, rules §18.4, §51.4).
 */

/* --------------------------------------------------------------- the reports */

export { REPORT_IDS, REPORT_MAX_RANGE_DAYS, REPORT_PERMISSIONS };

export type {
  CommunicationsReport,
  FunnelReport,
  InsurancePaymentsReport,
  IntegrationHealthReport,
  MetricDefinition,
  ReportBucket,
  ReportCell,
  ReportChart,
  ReportColumn,
  ReportEnvelope,
  ReportId,
  ReportRange,
  ReportSeriesRef,
  ReportTable,
};

/** The key the audited CSV route checks (`ADMIN_REPORTS_UI.md` open item 1). */
export const REPORTS_EXPORT_PERMISSION = 'reports.export';

export const REPORT_TITLES: Readonly<Record<ReportId, string>> = {
  funnel: 'Bookings and funnel',
  'insurance-payments': 'Insurance and payments',
  communications: 'Communications',
  'integration-health': 'Integration health',
};

/**
 * The route each report lives at. Used by the hub's cards and by the nav.
 *
 * The one place the API's `integration-health` and this app's `/reports/integrations`
 * are mapped, so renaming either side is a line here rather than a search.
 */
export const REPORT_PATHS = {
  funnel: '/reports/funnel',
  'insurance-payments': '/reports/insurance-payments',
  communications: '/reports/communications',
  'integration-health': '/reports/integrations',
} as const satisfies Record<ReportId, string>;

/* ------------------------------------------------------------- shared shapes */

/**
 * A pair of `yyyy-mm-dd` calendar days, inclusive, in the clinic's own zone.
 *
 * Distinct from the contracts' `ReportRange`, which is the **server's** echo and also
 * carries the grouping and the time zone it actually used (rules §51.7). This one is
 * what the range control works in before anything has been answered.
 */
export interface DateRange {
  from: string;
  to: string;
}

/**
 * How the range is cut into buckets.
 *
 * Day and week only, which is what the API accepts. A month grouping over a range capped
 * at 366 days produces at most twelve bars and loses the thing these reports are read
 * for — which Tuesday the drop-off started.
 */
export const GROUP_BY_OPTIONS = ['day', 'week'] as const;
export type GroupBy = ReportGroupBy;

/** Rendered from `charts[].unit` and `tables[].columns[].type`. */
export type ChartUnit = ReportChart['unit'];
export type ColumnType = ReportColumn['type'];
